import { useState } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import { useGetProfileQuery } from "../../../store/api/authApi";
import { useToast } from "../../../contexts/ToastContext";
import type { Payment } from "../../../store/api/paymentsApi";
import { formatDateTime } from "../../../utils/formatDate";
import {
	useGetPaymentQuery,
	useVerifyPaymentMutation,
} from "../../../store/api/paymentsApi";
import PaymentProofUploadModal from "../../tenant-portal/components/PaymentProofUploadModal";

interface PaymentDetailModalProps {
	payment: Payment | null;
	isOpen: boolean;
	onClose: () => void;
	onUpdate: () => void;
}

const PaymentDetailModal: React.FC<PaymentDetailModalProps> = ({
	payment,
	isOpen,
	onClose,
	onUpdate,
}) => {
	const { data: userProfile } = useGetProfileQuery();
	const isAdmin = userProfile?.role === "admin";
	const { data: paymentData, isLoading, refetch: refetchPayment } = useGetPaymentQuery(
		payment?.id || "",
		{
			skip: !payment?.id || !isOpen,
		},
	);

	const [verifyPayment, { isLoading: isVerifying }] =
		useVerifyPaymentMutation();
	const [verificationNotes, setVerificationNotes] = useState("");
	const [showVerifyForm, setShowVerifyForm] = useState(false);
	const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
	const { success, error: showError } = useToast();

	const currentPayment = paymentData || payment;

	const handleUploadSuccess = () => {
		setIsUploadModalOpen(false);
		success("Payment proof uploaded successfully");
		onUpdate();
	};

	if (!isOpen || !currentPayment) return null;

	const formatCurrency = (amount: number | undefined): string => {
		if (amount === undefined || amount === null || isNaN(amount)) {
			return "₦0.00";
		}
		return new Intl.NumberFormat("en-NG", {
			style: "currency",
			currency: "NGN",
		}).format(amount);
	};

	// Using centralized formatDateTime utility

	const handleVerify = async (verified: boolean) => {
		if (!currentPayment) {
			showError("Payment information is missing. Please refresh and try again.");
			return;
		}

		if (!currentPayment.id) {
			showError("Invalid payment ID. Please refresh and try again.");
			return;
		}

		try {
			await verifyPayment({
				id: currentPayment.id,
				data: {
					verified,
					verification_notes: verificationNotes.trim() || undefined,
				},
			}).unwrap();

			setShowVerifyForm(false);
			setVerificationNotes("");

			if (verified) {
				success(
					`Payment of ${formatCurrency(currentPayment.amount)} verified and marked as paid. Tenant has been notified.`
				);
			} else {
				success(
					`Payment proof rejected. Tenant has been notified to upload a new proof.`
				);
			}

			// Refresh payment data
			await refetchPayment();
			onUpdate();
		} catch (error: unknown) {
			console.error("Verification error:", error);
			let errorMessage = "Failed to verify payment. Please try again.";

			if (error && typeof error === "object") {
				if ("data" in error) {
					const errorData = error.data as { error?: string; message?: string };
					errorMessage = errorData.error || errorData.message || errorMessage;
				} else if ("message" in error && typeof error.message === "string") {
					errorMessage = error.message;
				}
			}

			showError(errorMessage);
		}
	};

	const normalizeStatus = (status: string | undefined): string => {
		if (!status || typeof status !== "string") return "";
		// Handle enum values like "PaymentStatus.PENDING"
		if (status.includes(".")) {
			const parts = status.split(".");
			return parts[parts.length - 1]?.toLowerCase() || "";
		}
		return status.toLowerCase();
	};

	const getStatusColor = (status: string | undefined): string => {
		const normalized = normalizeStatus(status);
		switch (normalized) {
			case "paid":
				return "bg-success/10 text-success";
			case "pending":
				return "bg-warning/10 text-warning";
			case "overdue":
				return "bg-error/10 text-error";
			case "cancelled":
			case "refunded":
				return "bg-muted text-muted-foreground";
			default:
				return "bg-muted text-muted-foreground";
		}
	};

	return (
		<>
			{/* Backdrop */}
			<div
				className="fixed inset-0 bg-black/50 z-1300 animate-fade-in"
				onClick={onClose}
			/>

			{/* Modal */}
			<div className="fixed inset-0 z-1400 flex items-center justify-center p-4">
				<div className="bg-card rounded-lg border border-border w-full max-w-3xl max-h-[90vh] overflow-hidden animate-scale-in">
					{/* Header */}
					<div className="flex items-center justify-between p-6 border-b border-border">
						<div>
							<h2 className="text-xl font-semibold text-foreground">
								Payment Details
							</h2>
							<p className="text-sm text-muted-foreground mt-1">
								Payment ID: {currentPayment.id}
							</p>
						</div>
						<Button variant="ghost" size="icon" onClick={onClose}>
							<Icon name="X" size={20} />
						</Button>
					</div>

					{/* Content */}
					<div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
						{isLoading ? (
							<div className="flex items-center justify-center py-12">
								<Icon
									name="Loader2"
									size={32}
									className="animate-spin text-primary"
								/>
							</div>
						) : (
							<div className="space-y-6">
								{/* Status Badge */}
								<div className="flex items-center justify-between">
									<span
										className={`px-3 py-1 rounded-full text-sm font-medium capitalize ${getStatusColor(
											currentPayment.status,
										)}`}
									>
										{normalizeStatus(currentPayment.status) || currentPayment.status || "Unknown"}
									</span>
									{currentPayment.payment_proof_url && (
										<span className="text-xs text-muted-foreground">
											Proof uploaded
										</span>
									)}
								</div>

								{/* Payment Information */}
								<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
									<div>
										<label className="text-sm font-medium text-muted-foreground">
											Tenant
										</label>
										<p className="text-lg text-foreground mt-1">
											{currentPayment.tenant_name || "N/A"}
										</p>
									</div>

									<div>
										<label className="text-sm font-medium text-muted-foreground">
											Property
										</label>
										{currentPayment.property_name ? (
											currentPayment.property_id ? (
												<a
													href={`/properties/${currentPayment.property_id}`}
													className="text-lg text-primary hover:underline mt-1 block"
												>
													{currentPayment.property_name}
												</a>
											) : (
												<p className="text-lg text-foreground mt-1">
													{currentPayment.property_name}
												</p>
											)
										) : (
											<p className="text-lg text-foreground mt-1">N/A</p>
										)}
									</div>

									<div>
										<label className="text-sm font-medium text-muted-foreground">
											Amount
										</label>
										<p className="text-lg font-semibold text-foreground mt-1">
											{formatCurrency(currentPayment.amount)}
										</p>
									</div>

									<div>
										<label className="text-sm font-medium text-muted-foreground">
											Due Date
										</label>
										<p className="text-lg text-foreground mt-1">
											{formatDateTime(currentPayment.due_date)}
										</p>
									</div>

									{currentPayment.paid_date && (
										<div>
											<label className="text-sm font-medium text-muted-foreground">
												Paid Date
											</label>
											<p className="text-lg text-foreground mt-1">
												{formatDateTime(currentPayment.paid_date)}
											</p>
										</div>
									)}

									{currentPayment.payment_method && (
										<div>
											<label className="text-sm font-medium text-muted-foreground">
												Payment Method
											</label>
											<p className="text-lg text-foreground mt-1 capitalize">
												{currentPayment.payment_method.replace("_", " ")}
											</p>
										</div>
									)}

									{currentPayment.reference_number && (
										<div>
											<label className="text-sm font-medium text-muted-foreground">
												Reference Number
											</label>
											<p className="text-lg text-foreground mt-1">
												{currentPayment.reference_number}
											</p>
										</div>
									)}
								</div>

								{/* Payment Proof */}
								<div>
									<label className="text-sm font-medium text-muted-foreground mb-2 block">
										Payment Proof
									</label>
									{currentPayment.payment_proof_url ? (
										<div className="border border-border rounded-lg p-4 bg-muted/30">
											<a
												href={currentPayment.payment_proof_url}
												target="_blank"
												rel="noopener noreferrer"
												className="flex items-center space-x-2 text-primary hover:text-primary/80 mb-2"
											>
												<Icon name="ExternalLink" size={16} />
												<span>View Payment Proof</span>
											</a>
											{currentPayment.payment_proof_uploaded_at && (
												<p className="text-xs text-muted-foreground mb-2">
													Uploaded:{" "}
													{formatDateTime(currentPayment.payment_proof_uploaded_at)}
												</p>
											)}
											{isAdmin && normalizeStatus(currentPayment.status) === "pending" && (
												<Button
													variant="outline"
													size="sm"
													onClick={() => setIsUploadModalOpen(true)}
												>
													<Icon name="Upload" size={16} className="mr-2" />
													Replace Proof
												</Button>
											)}
										</div>
									) : (
										<div className="border border-border rounded-lg p-4 bg-muted/30">
											<p className="text-sm text-muted-foreground mb-2">
												No payment proof uploaded
											</p>
											{(isAdmin || currentPayment.tenant_id === userProfile?.id) && (
												<Button
													variant="outline"
													size="sm"
													onClick={() => setIsUploadModalOpen(true)}
												>
													<Icon name="Upload" size={16} className="mr-2" />
													Upload Payment Proof
												</Button>
											)}
										</div>
									)}
								</div>

								{/* Verification Info (Admin) */}
								{isAdmin && currentPayment.verified_at && (
									<div className="border-t border-border pt-4">
										<h3 className="text-sm font-medium text-foreground mb-3">
											Verification Details
										</h3>
										<div className="space-y-2 text-sm">
											{currentPayment.verified_by_id && (
												<p className="text-muted-foreground">
													Verified by: {currentPayment.verified_by_id}
												</p>
											)}
											<p className="text-muted-foreground">
												Verified at: {formatDateTime(currentPayment.verified_at)}
											</p>
											{currentPayment.verification_notes && (
												<div>
													<p className="text-muted-foreground mb-1">Notes:</p>
													<p className="text-foreground">
														{currentPayment.verification_notes}
													</p>
												</div>
											)}
										</div>
									</div>
								)}

								{/* Admin Verification Form */}
								{isAdmin &&
									currentPayment.payment_proof_url &&
									normalizeStatus(currentPayment.status) === "pending" &&
									!currentPayment.verified_at &&
									!showVerifyForm && (
										<div className="border-t border-border pt-4">
											<Button
												onClick={() => setShowVerifyForm(true)}
												className="w-full"
											>
												<Icon name="CheckCircle" size={16} className="mr-2" />
												Verify Payment
											</Button>
										</div>
									)}

								{showVerifyForm && (
									<div className="border-t border-border pt-4 space-y-4">
										<h3 className="text-sm font-medium text-foreground">
											Verify Payment
										</h3>
										<Input
											label="Verification Notes"
											type="text"
											placeholder="Add notes about verification..."
											value={verificationNotes}
											onChange={(e) => setVerificationNotes(e.target.value)}
										/>
										<div className="flex items-center space-x-3">
											<Button
												onClick={() => handleVerify(true)}
												disabled={isVerifying}
												className="flex-1"
											>
												{isVerifying ? (
													<>
														<Icon
															name="Loader2"
															size={16}
															className="mr-2 animate-spin"
														/>
														Verifying...
													</>
												) : (
													<>
														<Icon
															name="CheckCircle"
															size={16}
															className="mr-2"
														/>
														Approve Payment
													</>
												)}
											</Button>
											<Button
												variant="outline"
												onClick={() => handleVerify(false)}
												disabled={isVerifying}
												className="flex-1"
											>
												<Icon name="X" size={16} className="mr-2" />
												Reject
											</Button>
											<Button
												variant="ghost"
												onClick={() => {
													setShowVerifyForm(false);
													setVerificationNotes("");
												}}
												disabled={isVerifying}
											>
												Cancel
											</Button>
										</div>
									</div>
								)}

								{/* Notes */}
								{currentPayment.notes && (
									<div className="border-t border-border pt-4">
										<label className="text-sm font-medium text-muted-foreground mb-2 block">
											Notes
										</label>
										<p className="text-foreground">{currentPayment.notes}</p>
									</div>
								)}
							</div>
						)}
					</div>

					{/* Footer */}
					<div className="p-6 border-t border-border bg-muted/20">
						<div className="flex items-center justify-end">
							<Button variant="outline" onClick={onClose}>
								Close
							</Button>
						</div>
					</div>
				</div>
			</div>

			{/* Payment Proof Upload Modal */}
			{currentPayment && (
				<PaymentProofUploadModal
					payment={currentPayment}
					isOpen={isUploadModalOpen}
					onClose={() => setIsUploadModalOpen(false)}
					onSuccess={handleUploadSuccess}
				/>
			)}
		</>
	);
};

export default PaymentDetailModal;
